import { useEffect, useRef, useState } from "react";
import { api, fmtMoney, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, KpiCard, DataTable, StatusBadge } from "../components/Shell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DatePicker } from "../components/DatePicker";
import { Label } from "../components/ui/label";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../components/ui/tabs";
import { UploadSimple, ReceiptX, UserCircle, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import { DancerForm } from "./Dancers";

export default function DancerPortal() {
  const { user } = useAuth();
  const dancerId = user.dancer_id;
  const [bal, setBal] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [requests, setRequests] = useState([]);
  const [payments, setPayments] = useState([]);
  const [profile, setProfile] = useState(null);
  const [invoice, setInvoice] = useState({ numero_facture: "", date_facture: "", facture_pdf_path: "" });
  const [uploading, setUploading] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    const [b, a, r, p, pr] = await Promise.all([
      api.get(`/dancers/${dancerId}/balance`),
      api.get(`/dancers/${dancerId}/accounts`),
      api.get(`/payment-requests`),
      api.get(`/payments`),
      api.get(`/me/profile`),
    ]);
    setBal(b.data);
    setAccounts(a.data);
    setRequests(r.data);
    setPayments(p.data);
    setProfile(pr.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const uploadPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setInvoice({ ...invoice, facture_pdf_path: r.data.path });
      toast.success("Facture PDF chargée");
    } catch (err) {
      toast.error("Échec upload");
    } finally {
      setUploading(false);
    }
  };

  const demander = async () => {
    if (!invoice.numero_facture || !invoice.date_facture) {
      toast.error("N° facture et date facture requis");
      return;
    }
    if (!invoice.facture_pdf_path) {
      toast.error("Téléversez votre facture PDF");
      return;
    }
    try {
      await api.post("/payment-requests", invoice);
      toast.success("Demande envoyée à la manageuse");
      setInvoice({ numero_facture: "", date_facture: "", facture_pdf_path: "" });
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  if (!bal) return <div className="text-[#a1a1aa]">Chargement...</div>;

  const peut_demander = bal.solde_disponible > 0 && !requests.some((r) => ["demande_envoyee", "a_payer"].includes(r.statut));

  return (
    <div>
      <PageHeader testId="portal-header" title="Mon espace" subtitle={`Bonjour ${user.full_name}`} />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <KpiCard label="Total gagné" value={fmtMoney(bal.total_brut)} />
        <KpiCard label="Total net" value={fmtMoney(bal.total_net_genere)} tone="jaunes" />
        <KpiCard label="Logement déduit" value={fmtMoney(bal.total_logement)} />
        <KpiCard label="Total payé" value={fmtMoney(bal.total_paye)} tone="success" />
        <KpiCard testId="kpi-solde" label="Solde disponible" value={fmtMoney(bal.solde_disponible)} tone={bal.solde_disponible > 0 ? "success" : "default"} />
      </div>

      <Tabs defaultValue="comptes">
        <TabsList className="bg-[#121217] border border-[#27272a]">
          <TabsTrigger value="comptes" data-testid="tab-mes-comptes">Mes comptes</TabsTrigger>
          <TabsTrigger value="demande" data-testid="tab-ma-demande">Ma demande de règlement</TabsTrigger>
          <TabsTrigger value="paiements" data-testid="tab-mes-paiements">Mes paiements</TabsTrigger>
          <TabsTrigger value="profil" data-testid="tab-mon-profil">Mon profil</TabsTrigger>
        </TabsList>

        <TabsContent value="comptes" className="mt-6">
          <DataTable
            columns={[
              { title: "Date", render: (a) => fmtDate(a.date_compte) },
              { title: "Billets", align: "right", render: (a) => <span style={{ color: "#34d399" }}>{a.billets}</span> },
              { title: "Jaunes", align: "right", render: (a) => <span style={{ color: "#fbbf24" }}>{a.jetons_jaunes}</span> },
              { title: "Rouges", align: "right", render: (a) => <span style={{ color: "#f87171" }}>{a.jetons_rouges}</span> },
              { title: "Bleus", align: "right", render: (a) => <span style={{ color: "#60a5fa" }}>{a.jetons_bleus}</span> },
              { title: "Acompte", align: "right", render: (a) => fmtMoney(a.acompte) },
              { title: "Net", align: "right", render: (a) => <b>{fmtMoney(a.total_net_apres_acompte)}</b> },
              { title: "Validation", render: (a) => <StatusBadge status={a.statut_validation} /> },
            ]}
            rows={accounts}
            rowKey="id"
          />
        </TabsContent>

        <TabsContent value="demande" className="mt-6">
          <div className="surface p-6 max-w-2xl">
            {peut_demander ? (
              <>
                <div className="label-xs mb-3">Demander le règlement total</div>
                <div className="mb-4 text-sm text-[#a1a1aa]">
                  Montant calculé automatiquement : <b className="text-white text-lg font-display">{fmtMoney(bal.solde_disponible)}</b>
                </div>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="label-xs">N° facture</Label>
                      <Input data-testid="inv-num" value={invoice.numero_facture} onChange={(e) => setInvoice({ ...invoice, numero_facture: e.target.value })} className="bg-[#09090b] border-[#27272a]" />
                    </div>
                    <div>
                      <Label className="label-xs">Date facture</Label>
                      <DatePicker testId="inv-date" value={invoice.date_facture} onChange={(v) => setInvoice({ ...invoice, date_facture: v })} />
                    </div>
                  </div>
                  <div>
                    <Label className="label-xs">Facture PDF</Label>
                    <input data-testid="inv-pdf" ref={fileRef} type="file" accept="application/pdf" onChange={uploadPdf} className="block w-full text-sm text-[#a1a1aa] file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-[#fbbf24] file:text-black file:font-medium hover:file:bg-[#f59e0b] mt-1" />
                    {uploading && <div className="text-xs text-[#71717a] mt-1">Chargement...</div>}
                    {invoice.facture_pdf_path && <div className="text-xs text-[#34d399] mt-1">PDF téléversé ✓</div>}
                  </div>
                  <Button data-testid="demander-reglement" onClick={demander} className="w-full bg-[#fbbf24] hover:bg-[#f59e0b] text-black">
                    <ReceiptX size={16} className="mr-2" /> Demander le règlement total
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-sm text-[#a1a1aa]">
                {bal.solde_disponible <= 0
                  ? "Aucun solde disponible à facturer pour le moment."
                  : "Une demande est déjà en cours de traitement."}
              </div>
            )}
          </div>

          <div className="mt-8">
            <div className="label-xs mb-3">Historique de mes demandes</div>
            <DataTable
              columns={[
                { title: "Date", render: (r) => fmtDate(r.date_demande) },
                { title: "Facture", key: "numero_facture" },
                { title: "Montant", align: "right", render: (r) => fmtMoney(r.montant_calcule) },
                { title: "Statut", render: (r) => (
                  <div className="flex items-center gap-2">
                    <StatusBadge status={r.statut} />
                    {r.en_retard && <StatusBadge status="en_retard" />}
                  </div>
                ) },
              ]}
              rows={requests}
              rowKey="id"
            />
          </div>
        </TabsContent>

        <TabsContent value="paiements" className="mt-6">
          <DataTable
            columns={[
              { title: "Date", render: (p) => fmtDate(p.date_reglement) },
              { title: "Montant", align: "right", render: (p) => fmtMoney(p.montant) },
              { title: "Mode", key: "mode_reglement" },
              { title: "Référence", key: "reference" },
            ]}
            rows={payments}
            rowKey="id"
          />
        </TabsContent>

        <TabsContent value="profil" className="mt-6">
          <div className="surface p-5 md:p-8 max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <UserCircle size={22} color="#fbbf24" weight="fill" />
              <div>
                <div className="font-display text-xl">Mon profil</div>
                <div className="text-xs text-[#71717a]">Informations administratives et bancaires</div>
              </div>
            </div>
            {profile && (
              <>
                <DancerForm
                  form={profile}
                  setForm={setProfile}
                  isNew={false}
                  disabledFields={["telephone"]}
                />
                <Button
                  data-testid="save-profile"
                  onClick={async () => {
                    setSavingProfile(true);
                    try {
                      const payload = { ...profile };
                      delete payload.telephone;
                      delete payload.mot_de_passe;
                      delete payload.notes;
                      delete payload.statut;
                      delete payload.id;
                      delete payload.date_creation;
                      delete payload.date_archivage;
                      // remove empty email to avoid validation error
                      if (!payload.email) delete payload.email;
                      const r = await api.put("/me/profile", payload);
                      setProfile(r.data);
                      toast.success("Profil mis à jour");
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Erreur");
                    } finally {
                      setSavingProfile(false);
                    }
                  }}
                  disabled={savingProfile}
                  className="mt-4 w-full h-12 bg-[#fbbf24] hover:bg-[#f59e0b] text-black font-semibold"
                >
                  <FloppyDisk size={18} weight="fill" className="mr-2" />
                  {savingProfile ? "Enregistrement..." : "Enregistrer mes informations"}
                </Button>
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
